import { SCENARIO_LIMITS } from "consts";

import type { CropYear, ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { withEmptyYearsPruned } from "pages/app/scenario-analysis/utils/scenarioBuilderState";

/** The scenario others are compared against: the one marked reference, else the first. */
export const getReferenceScenario = (scenarios: ScenarioData[]): ScenarioData | undefined => scenarios.find((scenario) => scenario.isReference) ?? scenarios[0];

/** A year the run can use: crop, tillage and residue are all set. */
export const isCropYearComplete = (year: CropYear): boolean => Boolean(year.crop && year.tillageId && year.residue != null);

/** Every year of the scenario is complete, blanks included. */
export const isScenarioComplete = (scenario: ScenarioData): boolean => scenario.years.every(isCropYearComplete);

/** Whether a field-import payload is complete enough to auto-run outcomes on the scenarios page. */
export const isFieldImportReadyForRecalculation = (cropYears: CropYear[]): boolean =>
  cropYears.length >= SCENARIO_LIMITS.MIN_YEARS && cropYears.every(isCropYearComplete);

/** The reference rotation has at least one year and every year has crop, tillage and residue. */
export const referenceYearsAreComplete = (scenarios: ScenarioData[]): boolean => {
  const reference = getReferenceScenario(scenarios);
  return !!reference && reference.years.length > 0 && isScenarioComplete(reference);
};

/** The reference could be calculated once its blank scaffold rows are pruned, as a recalculation submits it. */
export const referenceIsReadyToCalculate = (scenarios: ScenarioData[]): boolean => referenceYearsAreComplete(withEmptyYearsPruned(scenarios));

/** Every year of every scenario is complete, blanks included; recalculation is blocked otherwise. */
export const areScenariosReadyForRecalculation = (scenarios: ScenarioData[]): boolean => scenarios.every(isScenarioComplete);
